<template>
  <div class="modal-wrapper">
    <div v-if="!isNarrowWidthDevice" class="modal-mask"></div>
    <div v-if="!isNarrowWidthDevice" class="modal-content">
      <div class="modal-header" :class="{ 'show-warning': isReachLimit }">
        <div class="tool-search">
          <tool-search></tool-search>
        </div>
        <div class="tool-selected">
          <span @click="handleCancel">
            <fa icon="times"></fa>
          </span>
          <selected-tool-options></selected-tool-options>
        </div>
      </div>
      <div class="modal-body">
        <div class="tool-categories">
          <tool-categories></tool-categories>
        </div>
        <div class="tool-options">
          <tool-options></tool-options>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-outline-primary mr-3" @click="handleCancel">
          {{ $t('comparisonFeature.tools.selectionCancelBtn') }}
        </button>
        <button
          class="btn btn-primary"
          :disabled="
            comparisonToolList.length === 0 || !isComparisonToolListChanged
          "
          @click="handleApply"
        >
          {{ $t('comparisonFeature.tools.selectionApplyBtn') }}
        </button>
      </div>
    </div>
    <div v-else class="modal-content--narrow-width-device">
      <div class="modal-header">
        <span>
          {{ $t('comparisonFeature.tools.selectionModalTitleOnMobile') }}
        </span>
        <span @click="handleCancel">
          <fa icon="times"></fa>
        </span>
      </div>
      <div class="tool-search">
        <tool-search
          :stage="modalStage"
          @onModalStageChange="handleModalStageChange"
        ></tool-search>
      </div>
      <div class="tool-selected">
        <selected-tool-options></selected-tool-options>
      </div>
      <div class="modal-body">
        <div
          v-if="modalStage === 0"
          class="tool-categories"
          :class="getClassByToolLine()"
        >
          <tool-categories
            :stage="modalStage"
            @onModalStageChange="handleModalStageChange"
          ></tool-categories>
        </div>
        <div
          v-if="modalStage === 1"
          class="tool-options"
          :class="getClassByToolLine()"
        >
          <tool-options></tool-options>
        </div>
        <div v-if="modalStage === 1" class="modal-footer">
          <button class="btn btn-outline-primary mr-3" @click="handleCancel">
            {{ $t('comparisonFeature.tools.selectionCancelBtn') }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="
              comparisonToolList.length === 0 || !isComparisonToolListChanged
            "
            @click="handleApply"
          >
            {{ $t('comparisonFeature.tools.selectionApplyBtn') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import ToolSearch from './ToolSearch.vue';
import ToolCategories from './ToolCategories.vue';
import ToolOptions from './ToolOptions.vue';
import SelectedToolOptions from './SelectedToolOptions.vue';

export default {
  components: {
    ToolCategories,
    ToolOptions,
    SelectedToolOptions,
    ToolSearch,
  },

  data() {
    return {
      modalStage: 0,
    };
  },

  computed: {
    ...mapState([
      'comparisonToolList',
      'isNarrowWidthDevice',
      'selectedComparisonToolList',
    ]),

    // Check if current comparison tool list is the same as the selected
    isComparisonToolListChanged() {
      const stringifiedComparisonToolList = JSON.stringify(
        this.comparisonToolList
      );
      const stringifiedSelectedComparisonToolList = JSON.stringify(
        this.selectedComparisonToolList
      );

      return (
        stringifiedComparisonToolList !== stringifiedSelectedComparisonToolList
      );
    },

    isReachLimit() {
      return this.comparisonToolList.length === 6;
    },
  },

  methods: {
    ...mapMutations([
      'updateComparisonToolList',
      'updateComparisonToolQuery',
      'updateSelectedComparisonToolList',
      'updateComparisonToolCategory',
    ]),

    handleCancel() {
      if (this.isNarrowWidthDevice) {
        this.updateComparisonToolCategory('');
      } else {
        this.updateComparisonToolCategory('All');
      }
      this.updateComparisonToolList([]);
      this.updateComparisonToolQuery('');
      this.$emit('onCloseModal');
    },

    handleApply() {
      if (this.comparisonToolList.length === 0) return;

      this.updateSelectedComparisonToolList(this.comparisonToolList);
      this.$emit('onApplyComparisonToolChange');
      this.handleCancel();
    },

    handleModalStageChange(stage) {
      this.modalStage = stage;
    },

    getClassByToolLine() {
      // eslint-disable-next-line
      const isMobileDeviceInLandscapeMode = this.checkIsMobileDeviceInLandscapeMode();
      if (isMobileDeviceInLandscapeMode) {
        if (
          this.comparisonToolList.length === 1 ||
          this.comparisonToolList.length === 2 ||
          this.comparisonToolList.length === 3
        )
          return 'with-one-line-tools';

        if (
          this.comparisonToolList.length === 4 ||
          this.comparisonToolList.length === 5 ||
          this.comparisonToolList.length === 6
        )
          return 'with-two-line-tools';
      } else {
        if (
          this.comparisonToolList.length === 1 ||
          this.comparisonToolList.length === 2
        )
          return 'with-one-line-tools';

        if (
          this.comparisonToolList.length === 3 ||
          this.comparisonToolList.length === 4
        )
          return 'with-two-line-tools';

        if (
          this.comparisonToolList.length === 5 ||
          this.comparisonToolList.length === 6
        )
          return 'with-three-line-tools';
      }
    },

    checkIsMobileDeviceInLandscapeMode() {
      if (
        window.orientation === 90 ||
        window.orientation === -90 ||
        (screen && screen.orientation && screen.orientation.angle === 90) ||
        (screen && screen.orientation && screen.orientation.angle === -90)
      )
        return true;

      return false;
    },
  },
};
</script>

<style scoped lang="scss">
.modal-mask {
  position: fixed;
  z-index: 1000;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: rgba($color: #000000, $alpha: 0.7);
}

.modal-content {
  position: fixed;
  z-index: 1030;
  top: 3rem;
  left: 50%;
  right: 0;
  bottom: 3rem;
  width: calc(100vw - 6rem);
  max-width: 70rem;
  transform: translateX(-50%);
  margin: auto;
  background-color: white;
}

.modal-header {
  height: 7.5rem;
  padding: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: stretch;

  .tool-search {
    flex: 0 0 34%;
  }

  .tool-selected {
    position: relative;
    flex: 0 0 66%;

    & > span {
      position: absolute;
      top: 0.5rem;
      right: 1rem;
      font-size: 1.5rem;
      color: $icon-grey;
      cursor: pointer;
    }
  }
}

.show-warning {
  height: 8.5rem;
}

.modal-body {
  position: relative;
  display: flex;
  padding: 0;
  background-color: $background-grey;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: stretch;

  .tool-categories {
    flex: 33%;
    border-right: 1px solid #ccc;
  }

  .tool-options {
    overflow: auto;
    height: calc(100vh - 3rem - 4rem - 7.5rem - 5rem);
    flex: 67%;
  }
}

.modal-footer {
  height: 5rem;
  padding: 0 3rem;
}

.btn {
  width: 6rem;
  height: 3rem;
  line-height: 3rem;
  padding: 0;
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-content--narrow-width-device {
  position: fixed;
  z-index: 1030;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;

  .modal-header {
    height: 3rem;
    padding: 0 1rem;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    background-color: $text-light-blue;

    span {
      font-size: 1.25rem;
      font-weight: 600;
      color: #fff;
    }

    span:nth-child(2) {
      height: 3rem;
      width: 3rem;
      line-height: 3rem;
      text-align: center;
      font-size: 1.5rem;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .tool-search {
    height: 6.5rem;
  }

  .tool-selected {
    margin: 0 0 0 1rem;
  }

  .tool-categories {
    &.with-one-line-tools {
      min-height: calc(100vh - 3rem - 6.5rem - 4rem);
    }
    &.with-two-line-tools {
      min-height: calc(100vh - 3rem - 6.5rem - 7rem);
    }
    &.with-three-line-tools {
      min-height: calc(100vh - 3rem - 6.5rem - 10rem);
    }

    overflow: auto;
  }

  .tool-options {
    height: calc(100vh - 3rem - 6.5rem - 5rem);
    overflow: auto;
  }

  .with-one-line-tools {
    height: calc(100vh - 3rem - 6.5rem - 4rem - 3.25rem);
  }
  .with-two-line-tools {
    height: calc(100vh - 3rem - 6.5rem - 4rem - 6.25rem);
  }
  .with-three-line-tools {
    height: calc(100vh - 3rem - 6.5rem - 4rem - 9.25rem);
  }

  .modal-footer {
    position: fixed;
    display: flex;
    justify-content: center;
    background-color: white;
    z-index: 1;
    width: 100vw;
    height: 4rem;
    bottom: 0;
    left: 0;
  }
}
</style>
